/**
 * TaxiRatingPrompt — polls `/taxi/me/pending-ratings` and, if the rider
 * has a completed taxi trip they haven't rated yet, pops a 5-star rating
 * dialog. Also honours the `?rate_trip={id}` query param coming from the
 * push notification deep-link.
 *
 * Mounted once at the app root (Home page) — not inside any route guard —
 * so it fires regardless of which screen the rider lands on.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Star, Car } from "lucide-react";

export default function TaxiRatingPrompt() {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [trip, setTrip] = useState(null);
  const [stars, setStars] = useState(0);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const dismissedRef = useRef(new Set()); // in-memory session dismissals

  const openForTrip = useCallback((t) => {
    if (!t?.id || dismissedRef.current.has(t.id)) return;
    setTrip(t);
    setStars(0);
    setNote("");
    setOpen(true);
  }, []);

  // 1) Poll backend every 30 s for pending ratings
  useEffect(() => {
    if (!user?.id) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/taxi/me/pending-ratings");
        if (cancelled) return;
        const first = (data || []).find(t => !dismissedRef.current.has(t.id));
        if (first && !open) openForTrip(first);
      } catch { /* ignore */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user?.id, open, openForTrip]);

  // 2) Honour the deep-link ?rate_trip=<id> from push notifications
  useEffect(() => {
    const q = new URLSearchParams(loc.search);
    const id = q.get("rate_trip");
    if (!id || !user?.id) return;
    (async () => {
      try {
        const { data } = await api.get("/taxi/me/pending-ratings");
        const t = (data || []).find(x => x.id === id);
        if (t) openForTrip(t);
      } catch { /* ignore */ }
    })();
    // Clean the query so refreshes don't re-open
    q.delete("rate_trip");
    const next = q.toString();
    nav({ pathname: loc.pathname, search: next ? `?${next}` : "" }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.search, user?.id]);

  const submit = async () => {
    if (!trip?.id) return;
    if (stars < 1) { toast.error("اختر عدد النجوم"); return; }
    setBusy(true);
    try {
      await api.post(`/taxi/${trip.id}/rate`, { stars, note });
      toast.success("شكراً لتقييمك! ⭐");
      dismissedRef.current.add(trip.id);
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "فشل إرسال التقييم");
    } finally { setBusy(false); }
  };

  const skip = () => {
    if (trip?.id) dismissedRef.current.add(trip.id);
    setOpen(false);
  };

  if (!trip) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) skip(); else setOpen(true); }}>
      <DialogContent dir="rtl" className="max-w-sm" data-testid="taxi-rating-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <div className="size-10 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Star className="size-5 text-amber-500 fill-amber-500" />
            </div>
            كيف كانت رحلتك؟
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-xl bg-muted/50 p-3 flex items-center gap-3">
            <div className="size-10 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
              <Car className="size-5 text-primary" />
            </div>
            <div className="min-w-0">
              <div className="font-bold text-sm truncate">{trip.driver_name || "السائق"}</div>
              <div className="text-[11px] text-muted-foreground truncate">
                {trip.pickup_address ? `من ${trip.pickup_address}` : "رحلة مكتملة"}
                {trip.destination_address ? ` → ${trip.destination_address}` : ""}
              </div>
            </div>
          </div>

          {/* 5-star picker */}
          <div>
            <div className="text-center text-sm text-muted-foreground mb-2">
              {stars > 0 ? LABELS[stars - 1] : "اختر عدد النجوم"}
            </div>
            <div className="flex justify-center gap-1" dir="ltr" data-testid="rating-stars">
              {[1, 2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setStars(n)}
                  className="p-1 active:scale-90 transition-transform"
                  data-testid={`star-${n}`}
                >
                  <Star
                    className={`size-10 transition-colors ${n <= stars ? "fill-amber-400 text-amber-400" : "text-slate-300"}`}
                  />
                </button>
              ))}
            </div>
          </div>

          <Textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="اكتب ملاحظة (اختياري) — ما الذي أعجبك أو لم يعجبك؟"
            rows={3}
            data-testid="rating-note"
            maxLength={500}
          />

          <div className="flex gap-2">
            <Button variant="outline" onClick={skip} disabled={busy} className="flex-1" data-testid="rating-skip">
              لاحقاً
            </Button>
            <Button onClick={submit} disabled={busy || stars < 1} className="flex-1 bg-amber-500 hover:bg-amber-600" data-testid="rating-submit">
              {busy ? "جارٍ..." : "إرسال"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const LABELS = [
  "⭐ سيئة جداً",
  "⭐ ضعيفة",
  "⭐ مقبولة",
  "⭐ ممتازة",
  "⭐ رائعة!",
];
